// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, Station } from '../types';
import { squarify } from '../squarify';
import { attachSvgZoom } from '../svgZoom';
import { rateColor } from '../color';
import { median } from '../data';
import { fmtRate, fmtSigned, fmtNum, esc, attr } from '../format';
import { openDrawer } from '../drawer';
import { sectionHead } from './ui';

interface Metric { key: string; label: string; get: (s: Station) => number | null; fmt: (v: number | null) => string; dir: 1 | -1; unit: string }

const METRICS: Metric[] = [
  { key: 'rate', label: 'Fastest warming', get: (s) => s.rateMean, fmt: (v) => fmtRate(v), dir: 1, unit: '°C/decade' },
  { key: 'recent', label: 'Hottest recently', get: (s) => s.recentAnom, fmt: (v) => fmtSigned(v), dir: 1, unit: '°C vs 1961–1990' },
  { key: 'nights', label: 'Nights warming', get: (s) => s.rateMin, fmt: (v) => fmtRate(v), dir: 1, unit: '°C/decade' },
  { key: 'hot', label: 'Most hot days', get: (s) => s.latestH35, fmt: (v) => (v == null ? '—' : `${fmtNum(v)} / yr`), dir: 1, unit: 'days ≥ 35°C' },
  { key: 'frost', label: 'Losing frosts fastest', get: (s) => s.trendFrost, fmt: (v) => (v == null ? '—' : `${fmtSigned(v, 1, '')} nights/decade`), dir: -1, unit: 'change per decade' },
];

export function mount(el: HTMLElement, data: ClimateData): void {
  let metricKey = localStorage.getItem('warm.rank.metric') || 'rate';

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('Station leaderboards', 'Rank all 112 reference stations by the measure that matters to you. Rows above the median are shaded warm, below cool. Click any station to open its record.')}
      <div class="controls" data-metrics>
        ${METRICS.map((m) => `<button class="chip ${m.key === metricKey ? 'active' : ''}" data-metric="${m.key}">${m.label}</button>`).join('')}
      </div>
      <div class="rank-table" data-rank></div>
    </section>

    <section class="panel">
      ${sectionHead('Every station at a glance', 'A treemap of all stations — each tile sized by its total warming and coloured by its warming rate. Bigger, redder tiles have warmed most. Scroll to zoom; click a tile to open the record.')}
      <div class="treemap-wrap" data-tree></div>
    </section>`;

  const rankEl = el.querySelector('[data-rank]') as HTMLElement;
  const renderRank = () => {
    const m = METRICS.find((x) => x.key === metricKey)!;
    const rows = data.stations.filter((s) => m.get(s) != null).slice()
      .sort((a, b) => ((m.get(b) as number) - (m.get(a) as number)) * m.dir);
    const med = median(rows.map((s) => m.get(s) as number));
    const top = rows.slice(0, 30);
    const maxAbs = Math.max(...top.map((s) => Math.abs(m.get(s) as number)), 0.0001);
    rankEl.innerHTML = `<div class="rank-note">Median ${m.fmt(med)} · showing top 30 of ${rows.length}</div>` + top.map((s, i) => {
      const v = m.get(s) as number;
      const above = m.dir === 1 ? v >= med : v <= med;
      return `<button class="rank-row ${above ? 'above' : 'below'}" data-station="${s.id}">
        <span class="rank-i">${i + 1}</span>
        <span class="rank-name">${esc(s.name)}<span class="rank-state">${s.state}</span></span>
        <span class="rank-bar-wrap"><span class="rank-bar" style="width:${(Math.abs(v) / maxAbs) * 100}%"></span></span>
        <span class="rank-val">${m.fmt(v)}</span>
      </button>`;
    }).join('');
  };

  el.querySelector('[data-metrics]')?.addEventListener('click', (e) => {
    const b = (e.target as Element).closest('[data-metric]') as HTMLElement | null;
    if (!b) return;
    metricKey = b.getAttribute('data-metric')!;
    localStorage.setItem('warm.rank.metric', metricKey);
    el.querySelectorAll('[data-metric]').forEach((x) => x.classList.toggle('active', x === b));
    renderRank();
  });
  rankEl.addEventListener('click', (e) => {
    const r = (e.target as Element).closest('[data-station]') as HTMLElement | null;
    if (r) openDrawer(data, r.getAttribute('data-station')!);
  });
  renderRank();

  // Treemap
  const treeWrap = el.querySelector('[data-tree]') as HTMLElement;
  const items = data.stations.filter((s) => s.totalMean != null && s.rateMean != null)
    .map((s) => ({ s, v: Math.max(0.05, s.totalMean as number) }))
    .sort((a, b) => b.v - a.v);
  const W = 1000, H = 560;
  const rects = squarify(items.map((it) => it.v), W, H);
  let g = '';
  rects.forEach((r, i) => {
    const s = items[i].s;
    const showLabel = r.w > 64 && r.h > 22;
    g += `<g class="tm-cell" data-station="${s.id}" data-tip="${attr(`${s.name} (${s.state}) · warming ${fmtRate(s.rateMean)} · total ${fmtSigned(s.totalMean)}`)}">
      <rect x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${Math.max(0, r.w - 1).toFixed(1)}" height="${Math.max(0, r.h - 1).toFixed(1)}" fill="${rateColor(s.rateMean, 0.3)}" stroke="#fff" stroke-width="0.6"/>
      ${showLabel ? `<text x="${(r.x + 4).toFixed(1)}" y="${(r.y + 14).toFixed(1)}" class="tm-lbl">${esc(s.name.length > 16 ? s.name.slice(0, 15) + '…' : s.name)}</text>` : ''}
    </g>`;
  });
  treeWrap.innerHTML = `<svg viewBox="0 0 ${W} ${H}" class="treemap-svg" role="img" aria-label="Station treemap">${g}</svg>`;
  const svg = treeWrap.querySelector('svg') as SVGSVGElement | null;
  if (svg) attachSvgZoom(svg);
  treeWrap.addEventListener('click', (e) => {
    const c = (e.target as Element).closest('[data-station]') as Element | null;
    if (c) openDrawer(data, c.getAttribute('data-station')!);
  });
}
